import { ApiError, type DriverApiClient } from '../api/client';
import type { DriverActionResult } from '../api/types';
import type { DeliveryStorage } from '../db/storage';
import type { QueueItem } from './types';

/** Oplopende tussenpozen; de bezorger hoeft niets te doen (README §Offline-first). */
export const BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000, 120_000, 300_000] as const;

/** Verstuurde items blijven kort zichtbaar met de chip "verstuurd" en verdwijnen dan. */
export const SENT_RETENTION_MS = 5 * 60_000;

export function backoffFor(attempts: number): number {
  const index = Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1;
  return BACKOFF_MS[index]!;
}

export interface QueueEngineDeps {
  storage: DeliveryStorage;
  api: DriverApiClient;
  isOnline: () => boolean;
  now: () => number;
  onItemsChanged: (items: QueueItem[]) => void;
  onResult: (result: DriverActionResult) => void | Promise<void>;
}

/**
 * De wachtrij: elke handeling staat er eerst in en wordt daarna verstuurd, in volgorde van
 * vastlegging (aankomst hoort vóór aflevering). Idempotent op `clientActionId`; een
 * definitieve afwijzing wordt zichtbaar "lukt niet" met de serverreden en verdwijnt nooit
 * stil (contract §5.5/§5.6).
 */
export class QueueEngine {
  private items: QueueItem[] = [];
  private chain: Promise<void> = Promise.resolve();

  constructor(private readonly deps: QueueEngineDeps) {}

  async load(): Promise<void> {
    this.items = await this.deps.storage.listQueue();
    this.emit();
  }

  list(): QueueItem[] {
    return [...this.items].sort((a, b) => a.seq - b.seq);
  }

  /** Alles wat nog niet verstuurd is — dit is de teller "{n} nog te versturen". */
  pendingCount(): number {
    return this.items.filter((item) => item.state !== 'sent').length;
  }

  async enqueue(item: QueueItem): Promise<void> {
    this.items = [...this.items.filter((i) => i.clientActionId !== item.clientActionId), item];
    await this.deps.storage.putQueueItem(item);
    this.emit();
    void this.flush();
  }

  /** Terugdraaien mag alleen zolang het item nog niet onderweg is (B6). */
  async remove(clientActionId: string): Promise<boolean> {
    const item = this.items.find((i) => i.clientActionId === clientActionId);
    if (!item || item.state !== 'waiting') return false;
    this.items = this.items.filter((i) => i.clientActionId !== clientActionId);
    await this.deps.storage.deleteQueueItem(clientActionId);
    this.emit();
    return true;
  }

  /** "Nu proberen" (J1): backoff resetten en meteen doorgaan. Geruststelling, geen plicht. */
  async tryNow(): Promise<void> {
    const now = this.deps.now();
    await Promise.all(
      this.items
        .filter((item) => item.state === 'waiting')
        .map((item) => this.patch(item.clientActionId, { nextAttemptAt: now })),
    );
    await this.flush();
  }

  async clear(): Promise<void> {
    await Promise.all(
      this.items.map((item) => this.deps.storage.deleteQueueItem(item.clientActionId)),
    );
    this.items = [];
    this.emit();
  }

  /** Het eerstvolgende moment waarop er weer iets te doen valt, of `null`. */
  nextDueAt(): number | null {
    const waiting = this.items.filter((item) => item.state === 'waiting');
    if (waiting.length === 0) return null;
    return Math.min(...waiting.map((item) => item.nextAttemptAt));
  }

  /**
   * Verstuurronde. De rondes staan in één keten: wie `flush()` aanroept krijgt een belofte
   * die pas klaar is als zijn eigen ronde gedraaid heeft. Zo kan een `flush()` vlak na
   * "verbinding terug" nooit tussen wal en schip vallen.
   */
  flush(): Promise<void> {
    this.chain = this.chain.then(
      () => this.drain(),
      () => this.drain(),
    );
    return this.chain;
  }

  private async drain(): Promise<void> {
    await this.prune();
    if (!this.deps.isOnline()) return;

    for (const item of this.list()) {
      if (item.state === 'sent' || item.state === 'failed') continue;
      if (item.nextAttemptAt > this.deps.now()) return; // volgorde bewaren: niet vooruitlopen
      const keepGoing = await this.send(item);
      if (!keepGoing) return;
    }
  }

  /** `true` als de wachtrij door mag met het volgende item. */
  private async send(item: QueueItem): Promise<boolean> {
    await this.patch(item.clientActionId, { state: 'sending' });
    try {
      const result = await this.deps.api.postAction(item.routeId, item.request);
      await this.deps.onResult(result);
      await this.patch(item.clientActionId, {
        state: 'sent',
        error: null,
        errorCode: null,
        sentAt: new Date(this.deps.now()).toISOString(),
      });
      return true;
    } catch (error) {
      return this.handleFailure(item, error);
    }
  }

  private async handleFailure(item: QueueItem, error: unknown): Promise<boolean> {
    if (error instanceof ApiError && error.isDefinitive) {
      // Definitief: zichtbaar "lukt niet" met de serverreden. Nooit stil weggooien.
      if (error.result) await this.deps.onResult(error.result);
      await this.patch(item.clientActionId, {
        state: 'failed',
        error: error.message,
        errorCode: error.code,
      });
      return true;
    }

    const attempts = item.attempts + 1;
    await this.patch(item.clientActionId, {
      state: 'waiting',
      attempts,
      nextAttemptAt: this.deps.now() + backoffFor(attempts),
      error: null,
      errorCode: error instanceof ApiError ? error.code : null,
    });
    return false;
  }

  private async prune(): Promise<void> {
    const cutoff = this.deps.now() - SENT_RETENTION_MS;
    const stale = this.items.filter(
      (item) => item.state === 'sent' && item.sentAt !== null && Date.parse(item.sentAt) < cutoff,
    );
    if (stale.length === 0) return;
    for (const item of stale) await this.deps.storage.deleteQueueItem(item.clientActionId);
    this.items = this.items.filter((item) => !stale.includes(item));
    this.emit();
  }

  private async patch(clientActionId: string, changes: Partial<QueueItem>): Promise<void> {
    const index = this.items.findIndex((item) => item.clientActionId === clientActionId);
    if (index < 0) return;
    const updated: QueueItem = { ...this.items[index]!, ...changes };
    this.items = [...this.items.slice(0, index), updated, ...this.items.slice(index + 1)];
    await this.deps.storage.putQueueItem(updated);
    this.emit();
  }

  private emit(): void {
    this.deps.onItemsChanged(this.list());
  }
}
